// ============================================================================
// Adam Platform — Authenticated API Client
// ============================================================================
// Wraps fetch to automatically attach the JWT Bearer token from sessionStorage.
// If an access token has expired, refreshes the session once and retries.
// ============================================================================

const AUTH_KEY = "agentme-adam-auth";

interface AdamAuthState {
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
  email: string | null;
  role: string | null;
  sid: string | null;
  aal: number;
  stepupUntil: number | null;
}

interface AdamAuthClaims {
  sub?: string;
  email?: string;
  role?: string;
  sid?: string;
  aal?: number;
  stepup_until?: number | null;
}

interface AdamAuthTokenPayload {
  token?: string;
  accessToken?: string;
  refreshToken?: string | null;
  claims?: AdamAuthClaims;
}

/**
 * Structured API error — surfaces status code and server error message.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: { error?: string; details?: string } | null,
  ) {
    super(body?.error ?? `API error: ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

async function throwApiError(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  throw new ApiError(res.status, res.statusText, body);
}

/**
 * Get the stored auth state from sessionStorage.
 */
function getStoredAuth(): AdamAuthState {
  try {
    const stored = sessionStorage.getItem(AUTH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AdamAuthState>;
      return {
        token: parsed.token ?? null,
        refreshToken: parsed.refreshToken ?? null,
        userId: parsed.userId ?? null,
        email: parsed.email ?? null,
        role: parsed.role ?? null,
        sid: parsed.sid ?? null,
        aal: typeof parsed.aal === "number" ? parsed.aal : 0,
        stepupUntil: typeof parsed.stepupUntil === "number" ? parsed.stepupUntil : null,
      };
    }
  } catch {
    // ignore parse errors
  }
  return {
    token: null,
    refreshToken: null,
    userId: null,
    email: null,
    role: null,
    sid: null,
    aal: 0,
    stepupUntil: null,
  };
}

function persistAuth(state: AdamAuthState): void {
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(state));
}

function clearPersistedAuth(): void {
  sessionStorage.removeItem(AUTH_KEY);
}

function normalizeSessionPayload(
  payload: AdamAuthTokenPayload,
  fallback: AdamAuthState,
): AdamAuthState | null {
  const token = payload.token ?? payload.accessToken;
  if (!token) return null;

  const claims = payload.claims ?? {};
  return {
    token,
    refreshToken: payload.refreshToken ?? fallback.refreshToken,
    userId: claims.sub ?? fallback.userId,
    email: claims.email ?? fallback.email,
    role: claims.role ?? fallback.role,
    sid: claims.sid ?? fallback.sid,
    aal: claims.aal ?? fallback.aal,
    stepupUntil: claims.stepup_until ?? fallback.stepupUntil,
  };
}

function resolveRequestPath(input: RequestInfo | URL): string {
  const raw = typeof input === "string" ? input : input instanceof URL ? input.pathname : input.url;
  try {
    return new URL(raw, window.location.href).pathname;
  } catch {
    return raw;
  }
}

function isPublicAuthRoute(url: string): boolean {
  return (
    url.startsWith("/api/auth/register") ||
    url.startsWith("/api/auth/login") ||
    url.startsWith("/api/auth/passkeys/") ||
    url.startsWith("/api/auth/totp/") ||
    url.startsWith("/api/auth/session/refresh") ||
    url.startsWith("/api/auth/refresh") ||
    url.startsWith("/api/auth/introspect")
  );
}

function shouldAttemptRefreshFrom401(url: string, response: Response): Promise<boolean> {
  if (url.startsWith("/api/auth/")) return Promise.resolve(false);

  return response
    .clone()
    .json()
    .then((body) => {
      const error = String((body as { error?: unknown }).error ?? "")
        .trim()
        .toLowerCase();
      const message = String((body as { message?: unknown }).message ?? "")
        .trim()
        .toLowerCase();
      const authHeader = String(response.headers.get("www-authenticate") ?? "").toLowerCase();

      if (error === "step_up_required") return false;
      if (authHeader.includes("invalid_token")) return true;

      return (
        error === "unauthorized" &&
        (message === "invalid or expired token" || message === "bearer token required")
      );
    })
    .catch(() => false);
}

function shouldClearSessionFrom401(url: string, response: Response): Promise<boolean> {
  if (
    url.startsWith("/api/auth/session/refresh") ||
    url.startsWith("/api/auth/refresh") ||
    url.startsWith("/api/auth/introspect")
  ) {
    return Promise.resolve(true);
  }
  if (url.startsWith("/api/auth/")) return Promise.resolve(false);

  return response
    .clone()
    .json()
    .then((body) => {
      const error = String((body as { error?: unknown }).error ?? "")
        .trim()
        .toLowerCase();

      if (error === "step_up_required") return false;
      return false;
    })
    .catch(() => false);
}

function buildAuthenticatedRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string | null,
): Request {
  const headers = input instanceof Request ? new Headers(input.headers) : new Headers();
  const initHeaders = new Headers(init?.headers);
  initHeaders.forEach((value, key) => {
    headers.set(key, value);
  });

  const target =
    typeof input === "string" ? new URL(input, window.location.href).toString() : input;

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && init?.body) {
    headers.set("Content-Type", "application/json");
  }

  return new Request(target, { ...init, headers });
}

let refreshInFlight: Promise<AdamAuthState | null> | null = null;

async function refreshAuthSession(): Promise<AdamAuthState | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const auth = getStoredAuth();
      if (!auth.refreshToken) return null;

      const response = await fetch("/api/auth/session/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: auth.refreshToken }),
      });
      if (!response.ok) return null;

      const payload = (await response.json().catch(() => null)) as AdamAuthTokenPayload | null;
      if (!payload) return null;

      const nextAuth = normalizeSessionPayload(payload, auth);
      if (!nextAuth) return null;

      persistAuth(nextAuth);
      return nextAuth;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

function redirectToLogin(): void {
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

/**
 * Authenticated fetch — automatically attaches Bearer token header.
 * On expired-token 401 responses, refreshes once and retries before logout.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = resolveRequestPath(input);
  const auth = getStoredAuth();
  const protectedApiRoute = url.startsWith("/api/") && !isPublicAuthRoute(url);
  const request = buildAuthenticatedRequest(input, init, protectedApiRoute ? auth.token : null);

  const response = await fetch(request);

  if (response.status === 401 && protectedApiRoute) {
    const shouldRefresh = await shouldAttemptRefreshFrom401(url, response);
    if (shouldRefresh) {
      const refreshedAuth = await refreshAuthSession();
      if (refreshedAuth?.token) {
        const retryResponse = await fetch(
          buildAuthenticatedRequest(input, init, refreshedAuth.token),
        );
        if (retryResponse.status !== 401) {
          return retryResponse;
        }

        const retryNeedsRefresh = await shouldAttemptRefreshFrom401(url, retryResponse);
        const retryShouldLogout = await shouldClearSessionFrom401(url, retryResponse);
        if (!retryNeedsRefresh && !retryShouldLogout) {
          return retryResponse;
        }
      }

      clearPersistedAuth();
      redirectToLogin();
      return response;
    }

    const shouldLogout = await shouldClearSessionFrom401(url, response);
    if (shouldLogout) {
      clearPersistedAuth();
      redirectToLogin();
    }
  }

  return response;
}

/**
 * Convenience: GET JSON with auth.
 */
export async function apiGet<T>(url: string): Promise<T> {
  const res = await apiFetch(url);
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Convenience: POST JSON with auth.
 */
export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Convenience: PATCH JSON with auth.
 */
export async function apiPatch<T>(url: string, body: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

/**
 * Convenience: DELETE with auth.
 */
export async function apiDelete<T>(url: string): Promise<T> {
  const res = await apiFetch(url, { method: "DELETE" });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

// ─── F-014: platform_admin per-deployment JWT mint ────────────────────────
// Closes F-014 via option (b): rather than relaxing Hank's tenant-resolver
// to accept platform_admin tokens from any tenant, Adam UI mints a fresh
// Hank-audience token scoped to the target tenant immediately before
// navigation. Tokens TTL ~15min; no refresh path. Cache lives in
// useDeploymentToken — drop on expiry.

export interface DeploymentTokenResult {
  token: string;
  expiresAt: number;
  tenantId: string;
}

/**
 * Mint a deployment-scoped Hank-audience JWT for the current platform_admin
 * session. Throws ApiError on rate-limit (429), unknown tenant (400), or
 * non-admin caller (403). Returns the typed envelope verified against
 * src/shared/contracts/cp-platform-admin-deployment-token.ts.
 */
export async function mintDeploymentToken(tenantId: string): Promise<DeploymentTokenResult> {
  const body = await apiPost<unknown>("/api/auth/platform-admin/deployment-token", { tenantId });
  // Lightweight runtime shape check — fail closed (P3) if CP drifts.
  // We don't import the zod schema here to avoid pulling zod into the
  // Adam UI bundle; the shape is small enough to verify inline.
  const candidate = body as Partial<DeploymentTokenResult>;
  if (
    typeof candidate.token !== "string" ||
    typeof candidate.expiresAt !== "number" ||
    typeof candidate.tenantId !== "string"
  ) {
    throw new Error("Deployment token mint returned an unexpected shape");
  }
  return {
    token: candidate.token,
    expiresAt: candidate.expiresAt,
    tenantId: candidate.tenantId,
  };
}
